import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, KeyRound, Loader2, ShieldCheck, X, Lock, UserPlus, LogIn } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth, formatApiError } from "../context/AuthContext";
import { trackEmailSubmitted } from "../lib/analytics";

export default function AuthModal() {
  const {
    authModalOpen, setAuthModalOpen,
    authModalMode, setAuthModalMode,
    register, login, requestPasswordReset, resetPassword,
    setAgreementModalOpen, postAuthRedirect, setPostAuthRedirect,
  } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [resetStep, setResetStep] = useState("email"); // email | code
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!authModalOpen) {
      setLoading(false);
      setResetStep("email");
      setResetCode("");
      setNewPassword("");
    }
  }, [authModalOpen]);

  if (!authModalOpen) return null;

  const close = () => setAuthModalOpen(false);

  const onAfterAuth = (u) => {
    setAuthModalOpen(false);
    if (!u.agreement_signed) {
      setAgreementModalOpen(true);
    } else if (postAuthRedirect) {
      navigate(postAuthRedirect);
      setPostAuthRedirect(null);
    }
  };

  const handleRegister = async (e) => {
    e?.preventDefault();
    if (!email || password.length < 8 || fullName.trim().length < 2) return;
    setLoading(true);
    try {
      const u = await register({ email: email.trim().toLowerCase(),
                                 password, full_name: fullName.trim() });
      toast.success("Account created", { description: `Welcome, ${u.first_name || u.email}` });
      trackEmailSubmitted();
      onAfterAuth(u);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const handleLogin = async (e) => {
    e?.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    try {
      const u = await login({ email: email.trim().toLowerCase(), password });
      toast.success("Welcome back", { description: u.email });
      onAfterAuth(u);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const handleForgotRequest = async (e) => {
    e?.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await requestPasswordReset(email.trim().toLowerCase());
      setResetStep("code");
      toast.success("Reset code sent", { description: "Check your email for the 6-digit code." });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const handleResetVerify = async (e) => {
    e?.preventDefault();
    if (resetCode.length !== 6 || newPassword.length < 8) return;
    setLoading(true);
    try {
      const u = await resetPassword({
        email: email.trim().toLowerCase(),
        code: resetCode, new_password: newPassword,
      });
      toast.success("Password updated", { description: "You're now signed in." });
      onAfterAuth(u);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const isSignup = authModalMode === "signup";
  const isLogin = authModalMode === "login";
  const isForgot = authModalMode === "forgot";

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogle = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const GoogleButton = () => (
    <>
      <button type="button" onClick={handleGoogle} data-testid="google-signin-btn"
        className="w-full h-12 rounded-full border border-gray-300 bg-white hover:bg-gray-50 transition-colors flex items-center justify-center gap-3 font-semibold text-gray-700 text-sm">
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
        </svg>
        Continue with Google
      </button>
      <div className="flex items-center gap-3 my-4">
        <div className="flex-1 h-px bg-gray-200" />
        <span className="text-xs text-gray-400 font-medium">or</span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>
    </>
  );

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      data-testid="auth-modal"
      role="dialog" aria-modal="true"
      onClick={close}
    >
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-md bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
        <div className="relative p-8">
          <button onClick={close}
                  className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
                  aria-label="Close" data-testid="auth-modal-close">
            <X className="w-5 h-5 text-gray-500" />
          </button>

          <div className="flex items-center gap-3 mb-1">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-primary to-emerald-600 flex items-center justify-center shadow-md">
              {isSignup ? <UserPlus className="w-5 h-5 text-white" />
                : isLogin ? <LogIn className="w-5 h-5 text-white" />
                : <KeyRound className="w-5 h-5 text-white" />}
            </div>
            <div>
              <h2 className="font-playfair text-2xl font-bold text-gray-900">
                {isSignup ? "Create your account" :
                 isLogin ? "Welcome back" :
                 (resetStep === "email" ? "Reset password" : "Enter reset code")}
              </h2>
              <p className="text-sm text-gray-500">
                {isSignup ? "Start your herbal wellness journey." :
                 isLogin ? "Sign in to your Elite Herbalist account." :
                 (resetStep === "email"
                    ? "We'll email you a 6-digit code."
                    : `Code sent to ${email}. Expires in 10 min.`)}
              </p>
            </div>
          </div>

          {/* Tabs (signup/login switcher) */}
          {!isForgot && (
            <div className="flex gap-1 p-1 bg-gray-100 rounded-full mt-5 mb-4" data-testid="auth-tabs">
              <button
                onClick={() => setAuthModalMode("signup")}
                data-testid="auth-tab-signup"
                className={`flex-1 py-2 text-sm font-semibold rounded-full transition-all ${isSignup ? "bg-white shadow text-primary" : "text-gray-500 hover:text-gray-800"}`}
              >Sign Up</button>
              <button
                onClick={() => setAuthModalMode("login")}
                data-testid="auth-tab-login"
                className={`flex-1 py-2 text-sm font-semibold rounded-full transition-all ${isLogin ? "bg-white shadow text-primary" : "text-gray-500 hover:text-gray-800"}`}
              >Log In</button>
            </div>
          )}

          {/* SIGNUP */}
          {isSignup && (
            <div className="mt-3">
            <GoogleButton />
            <form onSubmit={handleRegister} className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-700 mb-1 block">Full name</label>
                <Input data-testid="signup-name-input" required minLength={2}
                       value={fullName} onChange={(e) => setFullName(e.target.value)}
                       placeholder="Jane Doe" className="h-11" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 mb-1 block">Email</label>
                <Input data-testid="signup-email-input" type="email" required
                       value={email} onChange={(e) => setEmail(e.target.value)}
                       placeholder="you@example.com" className="h-11" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 mb-1 block">Password (min 8 chars)</label>
                <Input data-testid="signup-password-input" type="password" required minLength={8}
                       value={password} onChange={(e) => setPassword(e.target.value)}
                       placeholder="••••••••" className="h-11" />
              </div>
              <Button type="submit" disabled={loading} data-testid="signup-submit-btn"
                className="w-full h-12 rounded-full bg-gradient-to-r from-primary to-emerald-600 text-white font-semibold shadow-lg shadow-primary/20">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Account"}
              </Button>
              <p className="text-xs text-gray-500 text-center pt-1 flex items-center justify-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5" />
                We'll email you a welcome &amp; agreement signing step.
              </p>
            </form>
            </div>
          )}

          {/* LOGIN */}
          {isLogin && (
            <div className="mt-3">
            <GoogleButton />
            <form onSubmit={handleLogin} className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-700 mb-1 block">Email</label>
                <Input data-testid="login-email-input" type="email" required
                       value={email} onChange={(e) => setEmail(e.target.value)}
                       placeholder="you@example.com" className="h-11" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 mb-1 block">Password</label>
                <Input data-testid="login-password-input" type="password" required
                       value={password} onChange={(e) => setPassword(e.target.value)}
                       placeholder="••••••••" className="h-11" />
              </div>
              <Button type="submit" disabled={loading} data-testid="login-submit-btn"
                className="w-full h-12 rounded-full bg-gradient-to-r from-primary to-emerald-600 text-white font-semibold shadow-lg shadow-primary/20">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
              </Button>
              <button type="button" onClick={() => setAuthModalMode("forgot")}
                data-testid="forgot-password-link"
                className="block mx-auto text-xs text-primary hover:underline pt-1">
                Forgot password?
              </button>
            </form>
            </div>
          )}

          {/* FORGOT */}
          {isForgot && resetStep === "email" && (
            <form onSubmit={handleForgotRequest} className="space-y-3 mt-3">
              <div>
                <label className="text-xs font-medium text-gray-700 mb-1 block">Email address</label>
                <Input data-testid="forgot-email-input" type="email" required
                       value={email} onChange={(e) => setEmail(e.target.value)}
                       placeholder="you@example.com" className="h-11" />
              </div>
              <Button type="submit" disabled={loading} data-testid="forgot-send-btn"
                className="w-full h-12 rounded-full bg-gradient-to-r from-primary to-emerald-600 text-white font-semibold">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset code"}
              </Button>
              <button type="button" onClick={() => setAuthModalMode("login")}
                className="block mx-auto text-xs text-gray-500 hover:text-gray-800 pt-1">
                ← Back to sign in
              </button>
            </form>
          )}

          {isForgot && resetStep === "code" && (
            <form onSubmit={handleResetVerify} className="space-y-3 mt-3">
              <div>
                <label className="text-xs font-medium text-gray-700 mb-1 block">6-digit code</label>
                <Input data-testid="reset-code-input" inputMode="numeric" required maxLength={6}
                       value={resetCode}
                       onChange={(e) => setResetCode(e.target.value.replace(/\D/g, ""))}
                       placeholder="123456"
                       className="h-12 text-center text-xl tracking-[0.4em] font-mono" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 mb-1 block">
                  New password (min 8 chars)
                </label>
                <Input data-testid="reset-newpw-input" type="password" required minLength={8}
                       value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                       placeholder="••••••••" className="h-11" />
              </div>
              <Button type="submit" disabled={loading || resetCode.length !== 6 || newPassword.length < 8}
                data-testid="reset-submit-btn"
                className="w-full h-12 rounded-full bg-gradient-to-r from-primary to-emerald-600 text-white font-semibold">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> :
                  (<span className="flex items-center gap-2"><Lock className="w-4 h-4" /> Update Password</span>)}
              </Button>
              <button type="button" onClick={() => setResetStep("email")}
                className="block mx-auto text-xs text-gray-500 hover:text-gray-800 pt-1">
                ← Send to a different email
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
